import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SPECIALTIES = [
  'Origin & Cause Analysis',
  'Electrical Fire Investigation',
  'Arson Investigation',
  'Vehicle Fire Analysis',
  'Wildfire Investigation',
  'Explosion Analysis',
  'Industrial Fire Investigation',
  'Residential Fire Analysis',
];

const CERTIFICATIONS = ['CFEI', 'CFII', 'CVFI', 'CFPS', 'PE', 'CFE'];

const Section = ({ title, count, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-t border-border first:border-none">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-sm font-semibold text-foreground flex items-center gap-2">
          {title}
          {count > 0 && (
            <span className="text-[10px] font-bold bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center">
              {count}
            </span>
          )}
        </span>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-muted-foreground" />
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

const CheckItem = ({ label, checked, onToggle }) => (
  <label className="flex items-center gap-2.5 cursor-pointer group py-0.5">
    <button
      type="button"
      onClick={onToggle}
      className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors
        ${checked ? 'bg-primary border-primary' : 'border-border group-hover:border-primary/60'}`}
    >
      {checked && (
        <svg viewBox="0 0 10 10" className="w-2.5 h-2.5" fill="none">
          <path d="M1.5 5l2.5 2.5L8.5 2" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
    <span className="text-sm text-foreground select-none" onClick={onToggle}>{label}</span>
  </label>
);

const FilterPanel = ({ filters, onFilterChange, onClearFilters, isOpen, onClose, resultCount }) => {
  const toggle = (key, val) => {
    const cur = filters[key] || [];
    onFilterChange(key, cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val]);
  };

  const activeCount =
    (filters.specialties?.length || 0) +
    (filters.certifications?.length || 0) +
    (filters.rateRange?.min ? 1 : 0) +
    (filters.rateRange?.max ? 1 : 0) +
    (filters.minExperience ? 1 : 0) +
    (filters.verifiedOnly ? 1 : 0);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[95] lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={`
          fixed right-0 top-0 h-full w-72 bg-card border-l border-border shadow-2xl
          lg:static lg:h-auto lg:w-full lg:border lg:rounded-xl lg:shadow-none
          z-[96] lg:z-auto
          flex flex-col overflow-hidden
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-border bg-muted/20 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Icon name="SlidersHorizontal" size={15} className="text-muted-foreground" />
            <span className="font-bold text-sm text-foreground">Filters</span>
            {activeCount > 0 && (
              <span className="text-[10px] font-bold bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center">
                {activeCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeCount > 0 && (
              <button
                type="button"
                onClick={onClearFilters}
                className="text-xs text-muted-foreground hover:text-destructive transition-colors"
              >
                Clear all
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="lg:hidden p-1 hover:bg-muted rounded-lg transition-colors"
              aria-label="Close filters"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Verified toggle */}
          <div className="px-4 py-4 border-b border-border">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => onFilterChange('verifiedOnly', !filters.verifiedOnly)}
            >
              <div>
                <p className="text-sm font-semibold text-foreground">Verified Experts Only</p>
                <p className="text-xs text-muted-foreground mt-0.5">Background-checked credentials</p>
              </div>
              <div className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${filters.verifiedOnly ? 'bg-primary' : 'bg-muted'}`}>
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${filters.verifiedOnly ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </div>
          </div>

          <Section title="Specialties" count={filters.specialties?.length || 0}>
            <div className="space-y-1">
              {SPECIALTIES.map((s) => (
                <CheckItem
                  key={s}
                  label={s}
                  checked={filters.specialties?.includes(s) || false}
                  onToggle={() => toggle('specialties', s)}
                />
              ))}
            </div>
          </Section>

          <Section title="Certifications" count={filters.certifications?.length || 0}>
            <div className="space-y-1">
              {CERTIFICATIONS.map((c) => (
                <CheckItem
                  key={c}
                  label={c}
                  checked={filters.certifications?.includes(c) || false}
                  onToggle={() => toggle('certifications', c)}
                />
              ))}
            </div>
          </Section>

          <Section title="Hourly Rate">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">$</span>
                <input
                  type="number"
                  min="0"
                  placeholder="Min"
                  value={filters.rateRange?.min || ''}
                  onChange={(e) => onFilterChange('rateRange', { ...filters.rateRange, min: e.target.value })}
                  className="w-full pl-6 pr-2 py-1.5 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <span className="text-muted-foreground text-sm">–</span>
              <div className="relative flex-1">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">$</span>
                <input
                  type="number"
                  min="0"
                  placeholder="Max"
                  value={filters.rateRange?.max || ''}
                  onChange={(e) => onFilterChange('rateRange', { ...filters.rateRange, max: e.target.value })}
                  className="w-full pl-6 pr-2 py-1.5 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
          </Section>

          <Section title="Experience">
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Minimum years</label>
              <input
                type="number"
                min="0"
                placeholder="Any"
                value={filters.minExperience || ''}
                onChange={(e) => onFilterChange('minExperience', e.target.value)}
                className="w-full px-3 py-1.5 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </Section>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-4 py-3 border-t border-border bg-muted/20">
          <p className="text-xs text-center text-muted-foreground mb-2">
            <span className="font-bold text-foreground">{resultCount ?? 0}</span>{' '}
            expert{resultCount !== 1 ? 's' : ''} match
          </p>
          <button
            type="button"
            onClick={() => { onClearFilters(); onClose(); }}
            className="w-full text-sm py-2 rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            Reset Filters
          </button>
        </div>
      </div>
    </>
  );
};

export default FilterPanel;
